import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, RefreshCw, ChevronRight, ChevronDown, Layers } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useWorkspaceStore } from "../stores/workspace.store";
import { useWorkspaceFilterGroupsStore } from "../stores/workspace-filter-groups.store";
import { useWorkspaceRole } from "../hooks/use-workspace-role";
import {
  workspaceFilterGroupsApi,
  WorkspaceFilterGroup,
  FilterGroupItem,
} from "../services/workspace-filter-groups.service";
import { fetchSheetData } from "../services/google-sheets.service";
import { pageApi } from "../services/page.service";
import { useToast } from "../hooks/use-toast";

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

interface FilterGroupsManagerModalProps {
  open: boolean;
  onClose: () => void;
}

export function FilterGroupsManagerModal({ open, onClose }: FilterGroupsManagerModalProps) {
  const { currentWorkspace } = useWorkspaceStore();
  const { setFilterGroups, updateFilterGroup, addFilterGroup, removeFilterGroup } =
    useWorkspaceFilterGroupsStore();
  const { isViewer } = useWorkspaceRole();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: allComponents = [] } = useQuery({
    queryKey: ["all-components", currentWorkspace?.id],
    queryFn: () => pageApi.getAllComponents(currentWorkspace!.id),
    enabled: !!currentWorkspace && open,
  });

  const tableComponents: any[] = (allComponents as any[]).filter((c: any) => c.type === "table");

  const [selectedFgId, setSelectedFgId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [rawValues, setRawValues] = useState<string[]>([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [fgNameInput, setFgNameInput] = useState("");
  const [groupNameInput, setGroupNameInput] = useState("");

  // ── Fetch filter groups ──────────────────────────────────────────────────────
  const { data: filterGroupsData, isLoading } = useQuery({
    queryKey: ["filter-groups", currentWorkspace?.id],
    queryFn: () => workspaceFilterGroupsApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace && open,
  });

  const filterGroups: WorkspaceFilterGroup[] = filterGroupsData ?? [];

  useEffect(() => {
    if (filterGroupsData !== undefined) setFilterGroups(filterGroupsData as WorkspaceFilterGroup[]);
  }, [filterGroupsData, setFilterGroups]);

  const selectedFg = filterGroups.find((fg) => fg.id === selectedFgId) ?? null;
  const selectedGroup = selectedFg?.groups.find((g) => g.id === selectedGroupId) ?? null;

  useEffect(() => {
    setSelectedGroupId(null);
    setRawValues([]);
    setFgNameInput(filterGroups.find((fg) => fg.id === selectedFgId)?.name ?? "");
  }, [selectedFgId]);

  useEffect(() => {
    const fg = filterGroups.find((fg) => fg.id === selectedFgId);
    setGroupNameInput(fg?.groups.find((g) => g.id === selectedGroupId)?.name ?? "");
  }, [selectedGroupId]);

  // Selected table component and its columns
  const selectedTableComponent = selectedFg?.dataSource?.tableComponentId
    ? tableComponents.find((c) => c.id === selectedFg.dataSource!.tableComponentId)
    : null;
  const tableColumns: any[] = selectedTableComponent?.config?.columns ?? [];

  // ── Mutations ────────────────────────────────────────────────────────────────
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["filter-groups", currentWorkspace?.id] });

  const createFgMutation = useMutation({
    mutationFn: (name: string) =>
      workspaceFilterGroupsApi.create(currentWorkspace!.id, { name }),
    onSuccess: (created) => {
      addFilterGroup(created);
      invalidate();
      setSelectedFgId(created.id);
    },
    onError: () => toast({ title: "Erro ao criar grupo de filtro", variant: "destructive" }),
  });

  const updateFgMutation = useMutation({
    mutationFn: (data: Partial<WorkspaceFilterGroup> & { id: string }) => {
      const { id, ...payload } = data;
      return workspaceFilterGroupsApi.update(currentWorkspace!.id, id, payload);
    },
    onSuccess: (updated) => {
      updateFilterGroup(updated);
      invalidate();
    },
    onError: () => toast({ title: "Erro ao salvar", variant: "destructive" }),
  });

  const deleteFgMutation = useMutation({
    mutationFn: (id: string) => workspaceFilterGroupsApi.remove(currentWorkspace!.id, id),
    onSuccess: (_r, id) => {
      removeFilterGroup(id);
      invalidate();
      if (selectedFgId === id) setSelectedFgId(null);
    },
    onError: () => toast({ title: "Erro ao excluir", variant: "destructive" }),
  });

  // ── Load raw values ──────────────────────────────────────────────────────────
  const loadRawValues = useCallback(async (fg: WorkspaceFilterGroup) => {
    const ds = fg.dataSource;
    if (!ds?.tableComponentId) {
      toast({ title: "Selecione uma tabela primeiro", variant: "destructive" });
      return;
    }
    if (!ds.sourceColumn) {
      toast({ title: "Selecione a coluna primeiro", variant: "destructive" });
      return;
    }
    const tableComp = tableComponents.find((c) => c.id === ds.tableComponentId);
    if (!tableComp) {
      toast({ title: "Tabela não encontrada", variant: "destructive" });
      return;
    }
    setLoadingValues(true);
    try {
      let rows: any[] = [];

      if ((tableComp.config?.dataSource?.data || []).length > 0) {
        rows = tableComp.config.dataSource.data;
      }

      if (rows.length === 0 && tableComp.config?.googleSheets?.enabled) {
        const spreadsheetId =
          tableComp.config.googleSheets.spreadsheetId as string | undefined;
        if (spreadsheetId) {
          const range = tableComp.config.googleSheets.range || "Sheet1";
          const { rows: r } = await fetchSheetData(spreadsheetId, range);
          rows = r;
        }
      }

      const unique = [
        ...new Set(
          rows
            .map((r: any) => r[ds.sourceColumn!])
            .filter((v: any) => v !== null && v !== undefined && v !== ""),
        ),
      ] as string[];

      if (unique.length === 0) {
        toast({ title: `Nenhum valor encontrado na coluna "${ds.sourceColumn}"`, variant: "destructive" });
      }
      setRawValues(unique.map(String));
    } catch {
      toast({ title: "Erro ao carregar valores", variant: "destructive" });
    } finally {
      setLoadingValues(false);
    }
  }, [tableComponents, toast]);

  // ── Group helpers ────────────────────────────────────────────────────────────
  const saveGroups = (fg: WorkspaceFilterGroup, newGroups: FilterGroupItem[]) => {
    updateFgMutation.mutate({ id: fg.id, groups: newGroups });
  };

  const handleAddGroup = (fg: WorkspaceFilterGroup) => {
    const newGroup: FilterGroupItem = { id: makeId(), name: "", values: [] };
    saveGroups(fg, [...fg.groups, newGroup]);
    setSelectedGroupId(newGroup.id);
  };

  const handleDeleteGroup = (fg: WorkspaceFilterGroup, groupId: string) => {
    saveGroups(fg, fg.groups.filter((g) => g.id !== groupId));
    if (selectedGroupId === groupId) setSelectedGroupId(null);
  };

  const handleRenameGroup = (fg: WorkspaceFilterGroup, groupId: string, name: string) => {
    saveGroups(fg, fg.groups.map((g) => (g.id === groupId ? { ...g, name } : g)));
  };

  const handleToggleValue = (
    fg: WorkspaceFilterGroup,
    groupId: string,
    rawValue: string,
    checked: boolean,
  ) => {
    const updated = fg.groups.map((g) => {
      if (checked) {
        if (g.id === groupId) return { ...g, values: [...g.values.filter((v) => v !== rawValue), rawValue] };
        return { ...g, values: g.values.filter((v) => v !== rawValue) };
      } else {
        if (g.id === groupId) return { ...g, values: g.values.filter((v) => v !== rawValue) };
        return g;
      }
    });
    saveGroups(fg, updated);
  };

  const toggleCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const isDataSourceReady = (fg: WorkspaceFilterGroup) => {
    const ds = fg.dataSource;
    return !!(ds?.tableComponentId && ds.sourceColumn);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[620px] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Grupos de Filtros
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 divide-x">

          {/* ── Col 1: Lista de grupos de filtro ────────────────────────────── */}
          <div className="w-52 flex flex-col shrink-0">
            {!isViewer && (
              <div className="px-3 py-2 border-b">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1 text-xs"
                  onClick={() => createFgMutation.mutate("Novo Grupo de Filtro")}
                  disabled={createFgMutation.isPending}
                >
                  <Plus className="w-3 h-3" />
                  Novo
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {isLoading && (
                <p className="text-xs text-muted-foreground px-3 py-4 text-center">Carregando...</p>
              )}
              {filterGroups.map((fg) => (
                <div
                  key={fg.id}
                  className={`group/item flex items-center gap-1 px-3 py-2 cursor-pointer text-sm hover:bg-muted/50 ${selectedFgId === fg.id ? "bg-accent" : ""}`}
                  onClick={() => setSelectedFgId(fg.id)}
                >
                  <span className="flex-1 truncate">{fg.name || "Sem nome"}</span>
                  {!isViewer && (
                    <button
                      className="opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Excluir "${fg.name}"?`)) deleteFgMutation.mutate(fg.id);
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              {!isLoading && filterGroups.length === 0 && (
                <p className="text-xs text-muted-foreground px-3 py-4 text-center">
                  Nenhum grupo criado
                </p>
              )}
            </div>
          </div>

          {/* ── Col 2: Configuração + grupos do filtro selecionado ───────────── */}
          {selectedFg ? (
            <div className="w-64 flex flex-col shrink-0">
              <div className="px-3 py-2 border-b space-y-2 overflow-y-auto">
                <Input
                  value={fgNameInput}
                  onChange={(e) => setFgNameInput(e.target.value)}
                  onBlur={() => {
                    if (fgNameInput !== selectedFg.name)
                      updateFgMutation.mutate({ id: selectedFg.id, name: fgNameInput });
                  }}
                  placeholder="Nome do grupo de filtro"
                  className="h-7 text-sm font-medium"
                  readOnly={isViewer}
                />

                <Select
                  value={selectedFg.dataSource?.tableComponentId || ""}
                  onValueChange={(val) =>
                    updateFgMutation.mutate({
                      id: selectedFg.id,
                      dataSource: { type: "table", tableComponentId: val, sourceColumn: "" },
                    })
                  }
                  disabled={isViewer}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue placeholder="Selecione uma tabela" />
                  </SelectTrigger>
                  <SelectContent>
                    {tableComponents.length === 0 ? (
                      <SelectItem value="_none" disabled>Nenhuma tabela disponível</SelectItem>
                    ) : (
                      tableComponents.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.config?.title || t.id}
                          {(t as any)._pageName ? ` — ${(t as any)._pageName}` : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>

                {selectedFg.dataSource?.tableComponentId && (
                  <Select
                    value={selectedFg.dataSource?.sourceColumn || ""}
                    onValueChange={(val) =>
                      updateFgMutation.mutate({
                        id: selectedFg.id,
                        field: val,
                        dataSource: { ...selectedFg.dataSource, sourceColumn: val },
                      })
                    }
                    disabled={isViewer}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Selecione uma coluna" />
                    </SelectTrigger>
                    <SelectContent>
                      {tableColumns.length === 0 ? (
                        <SelectItem value="_none" disabled>Nenhuma coluna</SelectItem>
                      ) : (
                        tableColumns.map((col: any) => (
                          <SelectItem key={col.field} value={col.field}>
                            {col.label || col.field}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Lista de grupos */}
              <div className="px-3 py-2 border-b">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1 text-xs"
                  onClick={() => handleAddGroup(selectedFg)}
                >
                  <Plus className="w-3 h-3" />
                  Adicionar Grupo
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto divide-y">
                {selectedFg.groups.map((group) => {
                  const isCollapsed = collapsedGroups.has(group.id);
                  return (
                    <div key={group.id}>
                      <div
                        className={`flex items-center gap-1 px-3 py-2 cursor-pointer hover:bg-muted/50 ${selectedGroupId === group.id ? "bg-accent/60" : ""}`}
                        onClick={() => setSelectedGroupId(group.id)}
                      >
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleCollapse(group.id); }}
                          className="shrink-0 text-muted-foreground"
                        >
                          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                        <span className="flex-1 text-sm truncate">{group.name || "Sem nome"}</span>
                        {group.values.length > 0 && (
                          <span className="text-xs text-muted-foreground shrink-0">{group.values.length}</span>
                        )}
                        <button
                          className="text-muted-foreground hover:text-destructive shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Excluir grupo "${group.name}"?`))
                              handleDeleteGroup(selectedFg, group.id);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {!isCollapsed && selectedGroupId === group.id && (
                        <div className="px-3 pb-2">
                          <Input
                            value={groupNameInput}
                            onChange={(e) => setGroupNameInput(e.target.value)}
                            onBlur={() => {
                              if (groupNameInput !== group.name)
                                handleRenameGroup(selectedFg, group.id, groupNameInput);
                            }}
                            placeholder="Nome do grupo"
                            className="h-6 text-xs"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {selectedFg.groups.length === 0 && (
                  <p className="text-xs text-muted-foreground px-3 py-4 text-center">
                    Nenhum grupo ainda
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="w-64 flex items-center justify-center shrink-0">
              <p className="text-sm text-muted-foreground text-center px-4">
                Selecione um grupo de filtros à esquerda
              </p>
            </div>
          )}

          {/* ── Col 3: Valores do grupo selecionado ─────────────────────────── */}
          <div className="flex-1 flex flex-col min-w-0">
            {selectedFg && selectedGroup ? (
              <>
                <div className="px-4 py-2 border-b flex items-center justify-between gap-2">
                  <Label className="text-xs text-muted-foreground truncate">
                    Valores para <strong>{selectedGroup.name || "este grupo"}</strong>
                  </Label>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs shrink-0"
                    onClick={() => loadRawValues(selectedFg)}
                    disabled={loadingValues || !isDataSourceReady(selectedFg)}
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingValues ? "animate-spin" : ""}`} />
                    Atualizar
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {rawValues.length === 0 && !loadingValues && (
                    <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6">
                      <p className="text-sm text-muted-foreground">
                        {isDataSourceReady(selectedFg)
                          ? "Clique em \"Atualizar\" para carregar os valores da fonte."
                          : "Configure a fonte de dados no painel ao lado para carregar os valores."}
                      </p>
                    </div>
                  )}
                  {rawValues.map((rawValue) => {
                    const isChecked = selectedGroup.values.includes(rawValue);
                    const isInOtherGroup =
                      !isChecked &&
                      selectedFg.groups.some(
                        (g) => g.id !== selectedGroup.id && g.values.includes(rawValue),
                      );
                    return (
                      <label
                        key={rawValue}
                        className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-muted/30 select-none border-b last:border-0"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) =>
                            handleToggleValue(selectedFg, selectedGroup.id, rawValue, e.target.checked)
                          }
                          className="w-4 h-4 shrink-0"
                        />
                        <span
                          className={`text-sm truncate flex-1 ${isInOtherGroup ? "text-muted-foreground" : ""}`}
                          title={rawValue}
                        >
                          {rawValue}
                        </span>
                        {isInOtherGroup && (
                          <span className="text-xs text-muted-foreground/60 shrink-0">outro grupo</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-muted-foreground text-center px-6">
                  {selectedFg
                    ? "Selecione ou crie um grupo para gerenciar os valores"
                    : "Selecione um grupo de filtros para começar"}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
